import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree,
} from '@angular/router';
import { RbacService } from '../rbac/rbac.service';
import { AuthService } from '../services/auth.service';
import { getPermissionsForRoute } from '../rbac/menu-permissions.config';

/**
 * Guard: authenticated user must have at least one of the required permissions for the route.
 * Use with AuthGuard: canActivate: [AuthGuard, PermissionGuard]
 * Route data overrides URL lookup: data: { permission: 'x' } or data: { permissions: ['x','y'] }
 * If no data, permissions are resolved from target URL via menu-permissions.config (protects direct URL access).
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {
  constructor(
    private auth: AuthService,
    private rbac: RbacService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    if (!this.auth.isAuthenticated()) {
      return this.router.createUrlTree(['/'], { queryParams: { returnUrl: state.url } });
    }
    const permission = route.data['permission'] as string | undefined;
    const permissions = route.data['permissions'] as string[] | undefined;
    let toCheck: string[] = permission ? [permission] : (permissions ?? []);
    if (toCheck.length === 0) {
      const path = state.url.split('?')[0].replace(/\/$/, '') || '/';
      toCheck = getPermissionsForRoute(path);
    }
    if (toCheck.length === 0) return true;
    return this.rbac.canAny(toCheck as any) ? true : this.router.createUrlTree(['/dashboard']);
  }
}
